import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Mail, Phone, MapPin, Globe, Pencil, Trash2,
  CreditCard, Building2,
  ExternalLink, Upload, Camera, ZoomIn, X, BadgeCheck,
  MessageCircle, Copy, Check, Send,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Badge, Modal, Label,
  Input, Select, Textarea, ConfirmDeleteModal,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useRoute } from '../lib/router';
import {
  fetchContact, updateContact, deleteContact, deletePortalUser,
  listMatters, listInvoices, invitePortalClient,
  uploadGhanaCard,
} from '../lib/db';
import { CONTACT_TYPES, GHANA_REGIONS } from '../lib/constants';
import { fullName, formatDate, formatMoney } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

// ─── Tone map ────────────────────────────────────────────────────────────────
const TYPE_TONE = {
  client:            'indigo',
  prospect:          'blue',
  opposing_counsel:  'red',
  witness:           'amber',
  expert:            'green',
  vendor:            'slate',
  court:             'slate',
  other:             'slate',
};

const MATTER_STATUS_TONE = {
  open:        'green',
  in_progress: 'blue',
  pending:     'amber',
  on_hold:     'slate',
  completed:   'green',
  closed:      'slate',
};

const INV_STATUS_TONE = {
  draft:   'slate',
  sent:    'blue',
  paid:    'green',
  overdue: 'red',
  partial: 'amber',
};

// ─── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value, href }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={14} className="text-slate-500 dark:text-slate-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 dark:text-slate-500">{label}</p>
        {href ? (
          <a href={href} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline break-all">{value}</a>
        ) : (
          <p className="text-sm text-slate-800 dark:text-slate-200 break-words">{value}</p>
        )}
      </div>
    </div>
  );
}

// ─── Ghana card image lightbox ────────────────────────────────────────────────
function CardLightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
        <X size={20} />
      </button>
      <img
        src={url}
        alt="Ghana card"
        className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ─── Edit contact modal (reused from ContactsPage) ────────────────────────────
function EditContactModal({ open, contact, firmId, onSaved, onClose }) {
  const blank = {
    first_name: '', last_name: '', organization: '', email: '', phone: '',
    contact_type: 'client', address: '', city: '', region: '', zip: '',
    ghana_card_number: '', ghana_card_url: '', notes: '',
  };
  const [form, setForm]           = useState(blank);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]       = useState(false);
  const [cardFile, setCardFile]   = useState(null);
  const [cardPreview, setCardPreview] = useState('');
  const cardUploadRef = useRef(null);
  const cardCameraRef = useRef(null);

  useEffect(() => {
    if (open && contact) {
      setForm({
        first_name:        contact.first_name        || '',
        last_name:         contact.last_name         || '',
        organization:      contact.organization      || '',
        email:             contact.email             || '',
        phone:             contact.phone             || '',
        contact_type:      contact.contact_type      || 'client',
        address:           contact.address           || '',
        city:              contact.city              || '',
        region:            contact.region            || '',
        zip:               contact.zip               || '',
        ghana_card_number: contact.ghana_card_number || '',
        ghana_card_url:    contact.ghana_card_url    || '',
        notes:             contact.notes             || '',
      });
      setCardFile(null);
      setCardPreview(contact.ghana_card_url || '');
      setFormErrors({});
    }
  }, [open, contact]);

  const handleCardFile = (file) => {
    if (!file) return;
    setCardFile(file);
    setCardPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    // Validate
    const errs = {};
    if (!form.phone.trim())   errs.phone   = 'Phone number is required.';
    if (!form.address.trim()) errs.address = 'Address is required.';
    const hasNumber = !!form.ghana_card_number.trim();
    const hasImage  = !!(cardFile || form.ghana_card_url);
    if (hasNumber || hasImage) {
      if (!hasNumber) errs.ghana_card_number = 'Card number is required when uploading a card.';
      if (!hasImage)  errs.ghana_card_image  = 'Card image is required when entering a card number.';
    }
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      const { data, error } = await updateContact(contact.id, form);
      if (error) { toast.error(error.message); return; }
      let saved = data;
      if (cardFile) {
        const { url, error: upErr } = await uploadGhanaCard(firmId, 'contacts', saved.id, cardFile);
        if (upErr) toast.error('Saved but card upload failed: ' + upErr.message);
        else if (url) {
          await updateContact(saved.id, { ghana_card_url: url });
          saved = { ...saved, ghana_card_url: url };
        }
      }
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit contact" size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={handleSave}>Save changes</Button></>}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
          <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Organization</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></div>
          <div><Label>Type</Label><Select options={CONTACT_TYPES} value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div>
            <Label>Phone <span className="text-red-500">*</span></Label>
            <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setFormErrors((p) => ({ ...p, phone: undefined })); }} className={formErrors.phone ? 'ring-red-400' : ''} />
            {formErrors.phone && <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>}
          </div>
          <div>
            <Label>Address <span className="text-red-500">*</span></Label>
            <Input value={form.address} onChange={(e) => { setForm({ ...form, address: e.target.value }); setFormErrors((p) => ({ ...p, address: undefined })); }} className={formErrors.address ? 'ring-red-400' : ''} />
            {formErrors.address && <p className="text-xs text-red-600 mt-1">{formErrors.address}</p>}
          </div>
          <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div>
            <Label>Region</Label>
            <Select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
              options={[{ value: '', label: 'Select region…' }, ...GHANA_REGIONS.map((r) => ({ value: r, label: r }))]} />
          </div>
          <div><Label>Postal code</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
        </div>

        {/* Ghana Card */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><CreditCard size={13} /> Ghana Card</p>
          <div>
            <Label>Ghana Card Number {(form.ghana_card_number || cardFile || form.ghana_card_url) && <span className="text-red-500">*</span>}</Label>
            <Input value={form.ghana_card_number} onChange={(e) => { setForm({ ...form, ghana_card_number: e.target.value }); setFormErrors((p) => ({ ...p, ghana_card_number: undefined })); }} placeholder="GHA-XXXXXXXXX-X" className={formErrors.ghana_card_number ? 'ring-red-400' : ''} />
            {formErrors.ghana_card_number && <p className="text-xs text-red-600 mt-1">{formErrors.ghana_card_number}</p>}
          </div>
          <div>
            <Label>Card image</Label>
            <input ref={cardUploadRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />
            <input ref={cardCameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />
            {cardPreview ? (
              <div className="mt-1 flex items-start gap-3">
                <img src={cardPreview} alt="Ghana card" className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover" />
                <div className="flex flex-col gap-1.5 mt-1">
                  <button type="button" onClick={() => cardUploadRef.current?.click()} className="text-xs text-indigo-600 hover:underline text-left">Replace</button>
                  <button type="button" onClick={() => { setCardFile(null); setCardPreview(''); setForm((f) => ({ ...f, ghana_card_url: '' })); }} className="text-xs text-red-500 hover:underline text-left">Remove</button>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex gap-2">
                <button type="button" onClick={() => cardUploadRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                  <Upload size={14} /> Upload
                </button>
                <button type="button" onClick={() => cardCameraRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                  <Camera size={14} /> Scan / take photo
                </button>
              </div>
            )}
            {formErrors.ghana_card_image && <p className="text-xs text-red-600 mt-2">{formErrors.ghana_card_image}</p>}
          </div>
        </div>

        <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ContactDetailPage({ contactId }) {
  const [, navigate]              = useRoute();
  const { firmId }                = useFirm();
  const log                       = useActivityLogger();

  const [contact,  setContact]    = useState(null);
  const [matters,  setMatters]    = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [loading,  setLoading]    = useState(true);

  const [showEdit,    setShowEdit]    = useState(false);
  const [showDelete,  setShowDelete]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [inviting,        setInviting]        = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLinkCopied,setInviteLinkCopied]= useState(false);
  const [lightbox,        setLightbox]        = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_formErrors, _setFormErrors] = useState({});

  useEffect(() => {
    if (!contactId) return;
    (async () => {
      setLoading(true);
      try {
        const [{ data: c }, { data: m }, { data: i }] = await Promise.all([
          fetchContact(contactId),
          firmId ? listMatters(firmId) : Promise.resolve({ data: [] }),
          firmId ? listInvoices(firmId) : Promise.resolve({ data: [] }),
        ]);
        setContact(c || null);
        setMatters((m || []).filter((x) => x.client_id === contactId));
        setInvoices((i || []).filter((x) => x.client_id === contactId));
      } catch (err) {
        console.error('ContactDetailPage fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [contactId, firmId]);

  const handleSaved = (updated) => {
    setContact(updated);
    setShowEdit(false);
    log('contact', updated.id, 'updated', `Contact "${fullName(updated) || updated.organization}" updated`);
  };

  const handleDelete = async () => {
    setDeleting(true);
    // Clean up the portal auth account if one exists before deleting the contact row.
    if (contact.portal_user_id) {
      await deletePortalUser(contact.id);
    }
    await deleteContact(contact.id);
    log('contact', contact.id, 'deleted', 'Contact removed');
    navigate('/contacts');
  };

  const portalInviteLink = contact
    ? `${window.location.origin}/#/portal-invite/${contact.portal_invite_token || contact.id}`
    : '';

  const sendEmailInvite = async () => {
    if (!contact.email) { toast.warning('This contact has no email address on file.'); return; }
    setInviting(true);
    const { error } = await invitePortalClient(contact.id);
    setInviting(false);
    if (error) { toast.error('Invite failed: ' + error.message); return; }
    setContact((c) => ({ ...c, portal_enabled: true, portal_invited_at: new Date().toISOString() }));
  };

  const sendWhatsAppInvite = () => {
    const phone = (contact.phone || '').replace(/\D/g, '');
    const clientName = fullName(contact) || contact.organization || 'there';
    const text = encodeURIComponent(
      `Hi ${clientName},\n\nYou've been invited to access your case files and invoices on our secure client portal.\n\nClick the link below to set up your account:\n${portalInviteLink}\n\nIf you have any questions, don't hesitate to reach out.`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(portalInviteLink);
    setInviteLinkCopied(true);
    setTimeout(() => setInviteLinkCopied(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-slate-500">Contact not found.</p>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/contacts')}>Back to contacts</Button>
      </div>
    );
  }

  const typeLabel = CONTACT_TYPES.find((t) => t.value === contact.contact_type)?.label || contact.contact_type;
  const addressLine = [contact.address, contact.city, contact.region, contact.zip].filter(Boolean).join(', ');
  const openMatters = matters.filter((m) => ['open', 'in_progress', 'pending'].includes(m.status));
  const totalBilled = invoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const totalDue    = invoices.filter((i) => ['sent', 'overdue', 'partial'].includes(i.status)).reduce((s, i) => s + Number(i.total || 0), 0);
  const isImage = contact.ghana_card_url?.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i);

  return (
    <div className="space-y-6">
      {/* Back + actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button
          onClick={() => navigate('/contacts')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition"
        >
          <ArrowLeft size={16} /> Contacts
        </button>
        <div className="flex items-center gap-2">
          {contact.contact_type === 'client' && (
            <div className="flex items-center gap-2">
              {contact.portal_enabled && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1 rounded-full">
                  <BadgeCheck size={12} /> Portal active
                </span>
              )}
              <Button variant="secondary" icon={Globe} size="sm" onClick={() => setShowInviteModal(true)}>
                {contact.portal_enabled ? 'Re-invite' : 'Invite to portal'}
              </Button>
            </div>
          )}
          <Button variant="secondary" icon={Pencil} size="sm" onClick={() => setShowEdit(true)}>Edit</Button>
          <Button variant="danger" icon={Trash2} size="sm" onClick={() => setShowDelete(true)}>Delete</Button>
        </div>
      </div>

      {/* Profile header */}
      <div className="flex items-start gap-5 flex-wrap">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
          {contact.organization && !contact.first_name ? (
            <Building2 size={28} className="text-indigo-600 dark:text-indigo-400" />
          ) : (
            <span className="text-indigo-700 dark:text-indigo-300 font-bold text-xl">
              {(contact.first_name?.[0] || '?')}{contact.last_name?.[0] || ''}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              {fullName(contact) || contact.organization || '—'}
            </h1>
            <Badge tone={TYPE_TONE[contact.contact_type] || 'slate'}>{typeLabel}</Badge>
          </div>
          {contact.organization && fullName(contact) && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{contact.organization}</p>
          )}
          <p className="text-xs text-slate-400 mt-1">Added {formatDate(contact.created_at)}</p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total matters',  value: matters.length },
          { label: 'Open matters',   value: openMatters.length },
          { label: 'Total billed',   value: formatMoney(totalBilled) },
          { label: 'Outstanding',    value: formatMoney(totalDue) },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardBody className="py-4">
              <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — contact info + Ghana card */}
        <div className="space-y-4">

          {/* Contact details */}
          <Card>
            <CardHeader title="Contact details" />
            <CardBody className="space-y-4">
              <InfoRow icon={Mail}    label="Email"   value={contact.email}   href={`mailto:${contact.email}`} />
              <InfoRow icon={Phone}   label="Phone"   value={contact.phone}   href={`tel:${contact.phone}`} />
              <InfoRow icon={MapPin}  label="Address" value={addressLine} />
              {contact.notes && (
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-slate-400 mb-1">Notes</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{contact.notes}</p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Ghana Card */}
          <Card>
            <CardHeader title="Ghana Card" />
            <CardBody className="space-y-4">
              {contact.ghana_card_number ? (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <CreditCard size={14} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Card number</p>
                    <p className="text-sm font-mono font-semibold text-slate-800 dark:text-slate-200 tracking-wider">
                      {contact.ghana_card_number}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No card number recorded</p>
              )}

              {contact.ghana_card_url ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-400">Card image</p>
                  {isImage ? (
                    <div className="relative group cursor-pointer" onClick={() => setLightbox(true)}>
                      <img
                        src={contact.ghana_card_url}
                        alt="Ghana card"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 object-cover max-h-48"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-xl flex items-center justify-center transition">
                        <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100 transition" />
                      </div>
                    </div>
                  ) : (
                    <a
                      href={contact.ghana_card_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      <ExternalLink size={13} /> View card document
                    </a>
                  )}
                  <button
                    onClick={() => setShowEdit(true)}
                    className="text-xs text-slate-400 hover:text-indigo-600 transition"
                  >
                    Replace image →
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-slate-400 italic mb-2">No card image uploaded</p>
                  <Button variant="secondary" size="sm" icon={Upload} onClick={() => setShowEdit(true)}>
                    Upload card
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Right column — matters + invoices */}
        <div className="lg:col-span-2 space-y-4">

          {/* Matters */}
          <Card>
            <CardHeader title={`Matters (${matters.length})`} />
            {matters.length === 0 ? (
              <CardBody>
                <p className="text-sm text-slate-400 text-center py-6">No matters linked to this contact.</p>
              </CardBody>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {matters.map((m) => (
                  <div key={m.id}
                    className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition"
                    onClick={() => navigate(`/matters/${m.id}`)}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{m.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{m.matter_number}{m.practice_area ? ` · ${m.practice_area}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge tone={MATTER_STATUS_TONE[m.status] || 'slate'}>
                        {m.status?.replace('_', ' ')}
                      </Badge>
                      <p className="text-xs text-slate-400">{formatDate(m.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Invoices */}
          <Card>
            <CardHeader title={`Invoices (${invoices.length})`} />
            {invoices.length === 0 ? (
              <CardBody>
                <p className="text-sm text-slate-400 text-center py-6">No invoices for this contact.</p>
              </CardBody>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Due {formatDate(inv.due_date)}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <Badge tone={INV_STATUS_TONE[inv.status] || 'slate'}>{inv.status}</Badge>
                      <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">
                        {formatMoney(inv.total || 0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Edit modal */}
      <EditContactModal
        open={showEdit}
        contact={contact}
        firmId={firmId}
        onSaved={handleSaved}
        onClose={() => setShowEdit(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDeleteModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        loading={deleting}
        title="Delete contact"
        description={<>Are you sure you want to permanently delete <strong>{fullName(contact) || contact.organization}</strong>? This cannot be undone.</>}
        onConfirm={handleDelete}
      />

      {/* Ghana card lightbox */}
      {lightbox && contact.ghana_card_url && (
        <CardLightbox url={contact.ghana_card_url} onClose={() => setLightbox(false)} />
      )}

      {/* Portal Invite Modal */}
      <Modal open={showInviteModal} title="Invite to Client Portal" onClose={() => { setShowInviteModal(false); setInviteLinkCopied(false); }}>
        <div className="space-y-5">
          {/* Intro */}
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Invite <span className="font-semibold text-slate-700 dark:text-slate-200">{fullName(contact) || contact.organization}</span> to access their case files, invoices, and receipts on the secure client portal.
          </p>

          {/* Option 1 — Email */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                <Send size={14} className="text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Email invite</p>
                <p className="text-xs text-slate-400">Sends a secure sign-up link to {contact.email || 'their email'}</p>
              </div>
            </div>
            {contact.email ? (
              <button
                onClick={async () => { await sendEmailInvite(); setShowInviteModal(false); }}
                disabled={inviting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
              >
                {inviting ? 'Sending…' : <><Send size={14} /> Send email invite</>}
              </button>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                ⚠ No email address on file. Add one in Edit Contact to use this option.
              </p>
            )}
          </div>

          {/* Option 2 — WhatsApp */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={14} className="text-[#25D366]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">WhatsApp</p>
                <p className="text-xs text-slate-400">Opens WhatsApp with a pre-written invite message</p>
              </div>
            </div>
            <button
              onClick={sendWhatsAppInvite}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-medium transition"
            >
              <MessageCircle size={14} /> Send via WhatsApp
            </button>
            {!contact.phone && (
              <p className="text-xs text-amber-600 dark:text-amber-400">⚠ No phone on file — WhatsApp will open without a pre-filled recipient.</p>
            )}
          </div>

          {/* Option 3 — Copy link */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                <Copy size={14} className="text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Copy invite link</p>
                <p className="text-xs text-slate-400">Share this link manually via any channel</p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 text-xs font-mono bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-500 dark:text-slate-400 truncate select-all">
                {portalInviteLink}
              </div>
              <button
                onClick={copyInviteLink}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition flex-shrink-0 ${
                  inviteLinkCopied
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                }`}
              >
                {inviteLinkCopied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
